import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { Feature, Geometry } from '@turf/turf';
import { map as lodashMap, chain, isEqual, find, each, includes } from 'lodash';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { VehicleInfoService } from 'src/app/modules/core/services/vehicle-info.service';
import { VehicleStore } from 'src/app/modules/core/store';
import { LineStore } from 'src/app/modules/core/store/line.store';
import { MapboxService } from 'src/app/modules/map/mapbox-map/mapbox-map.service';
import { LoggingService } from 'src/app/modules/shared/services/logging.service';
import { NotificationService } from 'src/app/modules/shared/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Layers } from 'src/app/modules/map/mapbox-map/models';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { CompactListColumn, CompactListItem, Vehicle, VehicleActivity } from 'src/app/modules/shared/models';
import { Line } from 'src/app/modules/shared/models/line';
import { SpinnerComponent } from '../../modules/shared/components/spinner/spinner.component';
import { TranslateModule } from '@ngx-translate/core';
import { CompactListComponent } from '../../modules/shared/components/compact-list/compact-list.component';
import { TabsComponent } from '../../modules/shared/components/tabs/tabs.component';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { AsyncPipe } from '@angular/common';
import { LineViewComponent } from './line-view/line-view.component';
import { LineViewService } from './line-view/line-view.service';
import { AuthService } from 'src/app/modules/core/services/auth.service';

@Component({
    selector: 'app-line',
    templateUrl: './line.component.html',
    styleUrls: ['./line.component.scss'],
    imports: [
        BladeHeaderComponent, TabsComponent, CompactListComponent, TranslateModule, 
        SpinnerComponent, AsyncPipe, LineViewComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class LineComponent implements OnInit, OnDestroy {
    bladeIndex: number;

    public lineId: string;
    public routeNumber: string;
    public garageCode: string[] = [];
    public componentState: LineComponentState;
    public line: Line;
    public vehicleColumns: CompactListColumn[];
    public vehicleItems: CompactListItem[];
    public drawingLineInMap = false;
    public noGeometries = false;
    public title$: Observable<string|undefined>;

    private _title$: BehaviorSubject<string|undefined> = new BehaviorSubject('');
    private _selectedVehicleInventoryNr: string;
    private _ngUnsubscribe$ = new Subject<void>();
    private _ngUnsubscribeLine$ = new Subject<void>();
    private _sourceComponentName = 'LineComponent';
    private _stateKey = 'line-component-state';
    private _fetchingGeometriesPromiseArray: Promise<VehicleActivity>[];
    private _isCentered = false;
    private _centerRetryTimeout: ReturnType<typeof setTimeout> | null = null;
    private _tripsByGarage: Record<string, string[]> = {};

    private _router = inject(Router);
    private _route = inject(ActivatedRoute);
    private _lineStore = inject(LineStore);
    private _bladeService = inject(BladeService);
    private _vehicleStore = inject(VehicleStore);
    private _vehicleInfoService = inject(VehicleInfoService);
    private _mapboxService = inject(MapboxService);
    private _loggingService = inject(LoggingService);
    private _notificationService = inject(NotificationService);
    private _translateService = inject(TranslateService);
    private _authService = inject(AuthService);
    protected readonly lineViewService = inject(LineViewService);
    protected readonly lineViewAccessEnabled = this._authService.hasAccessToOmsFunction('FeatureFlag Access');

    ngOnInit(): void {
        this.title$ = this._title$.asObservable();

        this._route.params.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            this._resetLineVehicleSelection();
            this.lineId = params['lineId'];
            this._isCentered = false;
            this._initLineSubscriptions();
        });

        // to set selection on the line
        this._bladeService.allOutletParams$.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            if (!params) return;
            const inventoryNumber = params['inventoryNumber'];
            this._selectedVehicleInventoryNr = inventoryNumber;

            this._resetSelection();

            if (inventoryNumber) {
                this._selectItem(inventoryNumber);
            }
        });

        this.componentState = this._getComponentState();

        this._initLineSubscriptions();
    }

    public ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        this._clearCenterRetry();
        this._unsubscribeLineSubscriptions();
        this._resetLineVehicleSelection();
    }

    public async changeTab(selectedTab: string) {
        this.componentState.selectedTab = selectedTab;
        this._updateComponentState();

        if (selectedTab === 'line-view' && this._authService.hasAccessToOmsFunction('FeatureFlag Access')) {
            const inventoryNumbers = (this.vehicleItems?.map(i => i.id) ?? []) as string[];
            this._vehicleStore.setInventoryFilter(inventoryNumbers.length > 0 ? inventoryNumbers : undefined);
            // Restart the internal vehicle stream (paused when switching away) and
            // pick up any vehicles that arrived while on another tab.
            await this.lineViewService.activateForLine(this.lineId, this.routeNumber, this.garageCode);
            this.lineViewService.refreshActivities(this.vehicleItems ?? []);
        } else {
            this.lineViewService.deactivate();
        }
    }

    public vehicleItemSelected(item: CompactListItem) {
        const encodedItemId = encodeURIComponent(item.id);
        const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right1:vehicle/${encodedItemId}`);
        if (indexOfItemId === -1) {
            this._router.navigate(['', { outlets: { 'outlet-right1': ['vehicle', item.id], 'outlet-right2': null } }]);
        }
    }

    public closeBlade() {
        const outlet = this._route.outlet;
        this._router.navigate(['', { outlets: { [outlet]: null, 'outlet-right1': null, 'outlet-right2': null } }]);
    }

    public vehicleColumnsUpdated(columns: CompactListColumn[]) {
        this._updateComponentState();
    }

    private _initLineSubscriptions() {
        this._unsubscribeLineSubscriptions();

        this._lineStore.getLineState(this.lineId).pipe(
            takeUntil(this._ngUnsubscribeLine$)
        ).subscribe((line: Line) => {
            this.line = line;
            this._resetLineInMap();
            this.noGeometries = false;
            if (!line) {
                this._router.navigate(['', { outlets: { 'outlet-right0': null, 'outlet-right1': null, 'outlet-right2': null } }]);
                return;
            }
            this.garageCode = line.garageCodes;
            this.routeNumber = line?.routeNumber;
            this._title$.next(`${line?.routeNumber} (${line.garageCodes})` || undefined);
            if (this._authService.hasAccessToOmsFunction('FeatureFlag Access')) {
                void this.lineViewService.activateForLine(this.lineId, line.routeNumber, line.garageCodes);
            }
        });

        this._vehicleStore.getVehiclesState().pipe(
            map(vehicles =>
                chain(vehicles)
                    .filter(vehicle => vehicle.lineId === this.lineId)
                    .map(vehicle => {

                        const item = new CompactListItem();
                        item.id = vehicle.inventoryNumber;
                        item.object = vehicle;
                        item.selected = this._selectedVehicleInventoryNr === item.id;
                        item.type = 'vehicle';
                        item.properties['inventoryNumber'] = vehicle.inventoryNumber;
                        item.properties['inventoryNumberWithoutPrefix'] = vehicle.inventoryNumberWithoutPrefix;
                        item.properties['garageCode'] = vehicle.garageCode;
                        item.properties['speed'] = vehicle.position?.speed;
                        item.properties['bearing'] = vehicle.position?.bearing;
                        item.properties['stop'] = vehicle.departureDeviation?.stopName;
                        item.properties['tripNumber'] = vehicle.tripNumber;
                        item.properties['blockNumber'] = vehicle.blockNumber;
                        item.properties['warnings'] = vehicle.displayableWarnings?.length ?? 0;
                        item.properties['deviation'] = vehicle.departureDeviation?.deviation.toShortString();
                        item.properties['deviationseconds'] = vehicle.departureDeviation?.deviation.getTotalSeconds();
                        item.properties['color'] = vehicle.departureDeviation?.color;

                        return item;
                    }).value()
            ),
            distinctUntilChanged(isEqual),
            takeUntil(this._ngUnsubscribeLine$)
        ).subscribe((items) => {
            this.vehicleItems = items;
            const inventoryNumbers = lodashMap(items, item => item.properties['inventoryNumber']);
            this._selectLineVehicles(inventoryNumbers);

            if (this.lineViewService.isActiveForLine(this.lineId)) {
                this._vehicleStore.setInventoryFilter(inventoryNumbers.length > 0 ? inventoryNumbers : undefined);
                this.lineViewService.refreshActivities(items);
            }

            this._centerMapOnItems(items);

            const tripsByGarage: Record<string, string[]> = {};
            for (const item of items) {
                const gc = item.properties['garageCode'] as string;
                const trip = item.properties['tripNumber'] as string;
                if (gc && trip) {
                    if (!tripsByGarage[gc]) tripsByGarage[gc] = [];
                    if (!tripsByGarage[gc].includes(trip)) tripsByGarage[gc].push(trip);
                }
            }
            if (!isEqual(this._tripsByGarage, tripsByGarage)) {
                this._tripsByGarage = tripsByGarage;
                this._getLineGeometriesAndRenderInMap(tripsByGarage);
            }
        });
    }

    private _unsubscribeLineSubscriptions() {
        this._resetLineInMap();
        this.vehicleItems = undefined;
        this.lineViewService.deactivate();
        this._vehicleStore.setInventoryFilter(undefined);
        this._clearCenterRetry();
        this._ngUnsubscribeLine$.next();
        this._ngUnsubscribeLine$.complete();
        this._ngUnsubscribeLine$ = new Subject();
    }

    private async _getLineGeometriesAndRenderInMap(tripsByGarage: Record<string, string[]>) {
        if (!tripsByGarage || Object.keys(tripsByGarage).length === 0) {
            return;
        }

        this.drawingLineInMap = true;

        const routeGeometryFeatures = await this._getRouteGeometryFeatures(tripsByGarage);
        this._renderShapesInMap(routeGeometryFeatures);
        this.drawingLineInMap = false;
    }

    private async _getRouteGeometryFeatures(tripsByGarage: Record<string, string[]>): Promise<Feature[]> {
        const geoJsonStrings: string[] = await this._getRouteGeometries(tripsByGarage);
        const features: Feature[] = [];
        if (geoJsonStrings) {
            geoJsonStrings.forEach(geoJson => {
                if (geoJson) {
                    const geometry: Geometry = JSON.parse(geoJson);

                    const properties = {
                        zIndex: 10,
                        type: 'shape'
                    };
                    features.push(turf.feature(geometry, properties));
                }
            });
        }
        return features;
    }

    private async _getRouteGeometries(tripsByGarage: Record<string, string[]>): Promise<string[]> {
        if (!this.routeNumber || !tripsByGarage) {
            return;
        }

        try {
            const results = await Promise.all(
                Object.entries(tripsByGarage).map(([garageCode, tripNumbers]) =>
                    firstValueFrom(this._vehicleInfoService.getRoutePaths(
                        garageCode, this.routeNumber.toString(), tripNumbers
                    ))
                )
            );
            return results.filter(Boolean).flat();
        } catch (error) {
            // eslint-disable-next-line max-len
            console.log(`Route Geometry for routeName: ${this.routeNumber}, trips by garage: ${JSON.stringify(tripsByGarage)} was not found`);
            return undefined;
        }

    }

    private _renderShapesInMap(shapeFeatures: Feature[]) {
        if (!shapeFeatures || shapeFeatures.length === 0) {
            this.noGeometries = true;
            this._notificationService.openError(this._translateService.instant('No GPS data found for the route'));
            return;
        }

        this.noGeometries = false;
        this._mapboxService.renderData(Layers.Lines, shapeFeatures, this._sourceComponentName);
    }

    private _resetLineInMap() {
        this.drawingLineInMap = false;
        this._mapboxService.renderData(Layers.Lines, [], this._sourceComponentName);

        // stop list of promises if any is still fetching when switching line
        lodashMap(this._fetchingGeometriesPromiseArray, promise => {
            if (promise) {
                Promise.resolve(promise);
            }
        });
    }

    private _clearCenterRetry() {
        if (this._centerRetryTimeout !== null) {
            clearTimeout(this._centerRetryTimeout);
            this._centerRetryTimeout = null;
        }
    }

    private _selectItem(id: string) {
        if (!this.vehicleItems) {
            return;
        }

        const found = find(this.vehicleItems, item => item.id === id);
        if (found) {
            found.selected = true;
        }
    }

    private _resetSelection() {
        // reset selection
        const selectedItems = chain(this.vehicleItems)
            .filter(item => item.selected)
            .value();
        each(selectedItems, item => item.selected = false);
    }

    private _selectLineVehicles(inventoryNumbers: string[]) {
        this._vehicleStore.selectVehicles(inventoryNumbers, this._sourceComponentName);
    }

    private _resetLineVehicleSelection() {
        this._vehicleStore.resetVehiclesSelection(this._sourceComponentName);
    }

    private _getComponentState(): LineComponentState {
        const storedState = localStorage.getItem(this._stateKey);
        const state = new LineComponentState();
        state.vehicleColumns = [
            ColumnFactory.getInventoryColumn({ sort: 'asc' }),
            ColumnFactory.getTripNumberColumn(),
            ColumnFactory.getBlockNumberColumn(),
            ColumnFactory.getDeviationColumn(),
            ColumnFactory.getNextStopColumn({ width: 100 }),
            ColumnFactory.getBearingColumn(),
            ColumnFactory.getWarningColumn()
        ];
        state.selectedTab = 'vehicles';
        state.tabs = this._authService.hasAccessToOmsFunction('FeatureFlag Access')
            ? ['vehicles', 'line-view']
            : ['vehicles'];

        // Get the selected tab from the localstorage and set as active if the tab exists in the tabs-list
        if (storedState) {
            const storedVehicleComponentState: LineComponentState = JSON.parse(storedState);
            const storedSelectedTab = storedVehicleComponentState.selectedTab;
            if (includes(state.tabs, storedSelectedTab)) {
                state.selectedTab = storedSelectedTab;
            }

            if (state.vehicleColumns) {
                const vehicleColumns = lodashMap(state.vehicleColumns, (column) => {
                    const storedColumn = find(storedVehicleComponentState.vehicleColumns, (c) => c.key === column.key);
                    if (storedColumn) {
                        delete column.sort;
                        delete storedColumn.width;
                        delete storedColumn.icon;
                    }
                    return { ...column, ...storedColumn };
                });
                state.vehicleColumns = vehicleColumns;
            }

        }
        return state;
    }

    private _updateComponentState() {
        localStorage.setItem(this._stateKey, JSON.stringify(this.componentState));
    }

    private _centerMapOnItems(items: CompactListItem[]) {

        const positions = lodashMap(items, (item) => {
            if (item.object?.position) {
                return (item.object as Vehicle)?.position;
            }
        });

        if (!this._isCentered && positions != undefined && positions.length > 0) {

            const invalidVehicles = items
                .filter(item => {
                    const pos = (item.object as Vehicle)?.position;
                    return pos && (!Number.isFinite(pos.latitude) || !Number.isFinite(pos.longitude));
                })
                .map(item => (item.object as Vehicle).inventoryNumber);

            if (invalidVehicles.length > 0) {
                this._loggingService.logEvent('InvalidLngLat_Line', {
                    routeNumber: this.routeNumber,
                    lineId: this.lineId,
                    invalidInventoryNumbers: invalidVehicles.join(','),
                });
            }

            if (!Number.isFinite(positions[0]?.latitude) || !Number.isFinite(positions[0]?.longitude)) {
                return;
            }

            const firstCoordinate = new mapboxgl.LngLat(positions[0].longitude, positions[0].latitude);

            const bounds = new mapboxgl.LngLatBounds([firstCoordinate, firstCoordinate]);

            each(positions, position => {
                if (position && Number.isFinite(position.longitude) && Number.isFinite(position.latitude)) {
                    const coordinate = new mapboxgl.LngLat(position.longitude, position.latitude);
                    bounds.extend(coordinate);
            }
        });

            const centered = this._mapboxService.fitBounds(bounds, 40);

            if (centered) {
                this._isCentered = true;
            } else {
                this._clearCenterRetry();
                this._centerRetryTimeout = setTimeout(() => this._centerMapOnItems(items), 300);
            }
        }
    }
}

export class LineComponentState {
    tabs: string[];
    selectedTab: string;
    vehicleColumns: CompactListColumn[];
}

